/** @format */
import axios, {
  AxiosError,
  AxiosRequestHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { useKey } from "hooks";
import { BASE_PATH, END_POINT, KEY_CONTEXT } from "utils";

const config = {
  baseURL: `${BASE_PATH}/api`,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const axiosClient = axios.create(config);

// Request Interceptor
axiosClient.interceptors.request.use(
  async (req: any) => {
    const { getKey } = useKey();
    const token = getKey(KEY_CONTEXT.ACCESS_TOKEN);
    if (token) {
      req.headers.Authorization = `Bearer ${token}`;
    }
    return req;
  },
  (err) => Promise.reject(err)
);

// Response Interceptor
axiosClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (err: AxiosError) => {
    const { getKey, setKeySite, removeKeySite } = useKey();
    const originalRequest = err.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (err.response?.status === 403 && !originalRequest._retry) {
      if (isRefreshing) {
        // Chờ refresh token xong rồi retry
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers = {
                ...originalRequest.headers,
                Authorization: `Bearer ${token}`,
              } as AxiosRequestHeaders;
              resolve(axiosClient(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const token = getKey(KEY_CONTEXT.ACCESS_TOKEN);
        const rs = await axios.post(
          `${BASE_PATH}/api${END_POINT.AUTH.REFRESH_TOKEN}`,
          {},
          {
            headers: {
              ...config.headers,
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const { data, statusCode } = rs.data;

        if (statusCode === 200) {
          setKeySite({ accessToken: data.accessToken });
          processQueue(null, data.accessToken);

          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${data.accessToken}`,
          } as AxiosRequestHeaders;

          return axiosClient(originalRequest);
        }

        processQueue(new Error("Refresh token failed"), null);
        removeKeySite();
        return Promise.reject(err);
      } catch (refreshError) {
        processQueue(refreshError, null);
        removeKeySite();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err);
  }
);

export default axiosClient;

export const postRequest = (url: string, param: any, conf: any = null) =>
  axiosClient.post(url, param, conf || undefined);

export const deleteRequest = (url: string) => axiosClient.delete(url);

export const patchRequest = (url: string, param: any, conf: any = null) =>
  axiosClient.patch(url, param, conf || undefined);

export const getRequest = (url: string, conf: any = null) =>
  axiosClient.get(url, conf || undefined);
